import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { StringFormatter } from '@repo/toolkit/string'
import Badge from '@repo/ui/badge'
import {
  EmailIcon,
  EmergencyIcon,
  FatherIcon,
  HomeIcon,
  LocationOneIcon,
  MeetingIcon,
  MotherIcon,
  TelephoneIcon,
  TravelTwoIcon
} from '@repo/ui/icons'
import Typography from '@repo/ui/typography'

import useAffectedStaff from '../../../../../lib/hooks/use-affected-staff'
import useTranslate from '../../../../../lib/hooks/use-translate'
import { AccomodationEnum } from '../../../../../lib/types/enums/accomodation-type/accomodation-type'
import { maritalStatusEnumMapping } from '../../../../../lib/types/enums/staff-application/marital-status.enum'
import { AffectedStaffProperties } from '../../../../../lib/types/models/affectedStaff/properties.affected-staff'
import { formatPhoneNumber } from '../../../../../utils/functions/format-phone-number'
import IconBox from '../../../../elements/icon-box'
import OverviewCard from '../../../client-space/overview-card'
import StaffPersonalSpaceCenterOlJourneyPlaceSection from './staff-personal-space-center-ol-journey-place-section'
import StaffPersonalSpaceForeignTravelChoiceSection from './staff-personal-space-foreign-travel-choice-section'
import StaffPersonalSpaceJourneySection from './staff-personal-space-journey-section'
import StaffPersonalSpaceLocalOrganizerSection from './staff-personal-space-local-organizer-section'
import StaffPersonalSpaceMyTeamSection from './staff-personal-space-my-team-section'
import StaffPersonalSpaceRadSection from './staff-personal-space-rad-section'
import StaffPersonalSpaceTripSection from './staff-personal-space-trip-section'
import StaffPersonalSpaceUrgentNumberSection from './staff-personal-space-urgent-number-section'

const StaffPersonalSpaceJourneyDetailsHome = () => {
  const { t } = useTranslate()
  const { affectedStaff } = useAffectedStaff()

  const staffRequest = affectedStaff?.staffRequest
  const hostFamily = affectedStaff?.hostFamily

  if (!affectedStaff) return null

  // Foreign staff have no train/bus itinerary: their "Voyage" block is how they join the group
  const isFrench = AffectedStaffProperties.isFrench(affectedStaff)

  return (
    <div className="flex flex-col gap-4">
      <Typography variant="h3" color={tailwindColors.black.DEFAULT}>
        {t('staff.personal.space.my.journeys.details.tab.home.title')}
      </Typography>
      {staffRequest && (
        <IconBox
          mode="inner-box"
          className="gap-3"
          icon={TravelTwoIcon}
          title={t('staff.personal.space.my.journeys.details.tab.home.journey.info.title')}
        >
          <div className="grid grid-cols-2 gap-3 mobile:grid-cols-1">
            <StaffPersonalSpaceJourneySection
              showCenter={affectedStaff.courseLocationDiffused}
              staffRequest={staffRequest}
            />
            <div className="flex flex-col gap-3">
              {isFrench && affectedStaff.travelInformationDiffused && (
                <StaffPersonalSpaceTripSection trip={affectedStaff.trip} />
              )}
              {!isFrench && affectedStaff.travelChoiceDiffused && (
                <StaffPersonalSpaceForeignTravelChoiceSection />
              )}
              {affectedStaff.courseLocationDiffused && (
                <StaffPersonalSpaceCenterOlJourneyPlaceSection
                  centerIds={staffRequest.centers.map((c) => c.id)}
                />
              )}
              {affectedStaff.olInfoDiffused &&
                staffRequest.centers
                  .filter((c) => c.accomodation === AccomodationEnum.HOST_FAMILY && !!c.ol)
                  .map((center) =>
                    center.ol ? (
                      <StaffPersonalSpaceLocalOrganizerSection key={center.id} ol={center.ol} />
                    ) : null
                  )}
            </div>
          </div>
        </IconBox>
      )}

      {affectedStaff.meetingDetailsDiffused && affectedStaff?.associatedRad && (
        <IconBox
          mode="inner-box"
          icon={MeetingIcon}
          title={t('staff.personal.space.my.journeys.details.tab.home.rad.title')}
        >
          <div className="h-3"></div>
          <StaffPersonalSpaceRadSection rad={affectedStaff?.associatedRad} />
        </IconBox>
      )}

      {affectedStaff.emergencyContactsDiffused && (
        <IconBox
          mode="inner-box"
          className="gap-3"
          icon={EmergencyIcon}
          title={t('staff.personal.space.my.journeys.details.tab.home.urgent.number.title')}
        >
          <StaffPersonalSpaceUrgentNumberSection affectedStaffId={affectedStaff.id} />
        </IconBox>
      )}

      {affectedStaff.teamDiffused && <StaffPersonalSpaceMyTeamSection />}

      {affectedStaff.hostFamilyAccommodationDiffused &&
        staffRequest?.centers.map((c) =>
          c.accomodation === AccomodationEnum.HOST_FAMILY ? (
            <IconBox
              mode="inner-box"
              className="gap-3"
              key={c.id}
              icon={HomeIcon}
              title={t(
                'staff.personal.space.my.journeys.details.tab.home.contact.details.host.family'
              )}
            >
              {hostFamily ? (
                <OverviewCard
                  subtitle={
                    <div className="flex items-center gap-3 mb-4">
                      <Typography variant="sub-heading" color={tailwindColors.grey.DEFAULT}>
                        {hostFamily.familyName}
                      </Typography>
                      <Badge
                        variant="secondaryRed"
                        text={t(maritalStatusEnumMapping[hostFamily.maritalStatus])}
                      />
                    </div>
                  }
                  bulletPoints={[
                    {
                      label: t(
                        'staff.personal.space.my.journeys.details.tab.home.contact.details.host.family.mrs'
                      ),
                      variant: 'title'
                    },
                    {
                      icon: MotherIcon,
                      label: `${hostFamily.parent1FirstName ? StringFormatter.capitalize(hostFamily.parent1FirstName) : ''} (${hostFamily.parent1Position})`
                    },
                    {
                      icon: TelephoneIcon,
                      label: valueRender(hostFamily.parent1PhoneNumber, (phone) =>
                        formatPhoneNumber(phone)
                      )
                    },
                    {
                      icon: EmailIcon,
                      label: hostFamily.parent1Email
                    },
                    {
                      label: t(
                        'staff.personal.space.my.journeys.details.tab.home.contact.details.host.family.mr'
                      ),
                      variant: 'title'
                    },
                    {
                      icon: FatherIcon,
                      label: `${hostFamily.parent2FirstName ? StringFormatter.capitalize(hostFamily.parent2FirstName) : ''} ${hostFamily.parent2Position ?? ''}`
                    },
                    {
                      icon: TelephoneIcon,
                      label: valueRender(hostFamily.parent2PhoneNumber, (phone) =>
                        formatPhoneNumber(phone)
                      )
                    },
                    {
                      icon: EmailIcon,
                      label: valueRender(hostFamily.parent2Email)
                    }
                  ]}
                  imagePath="/family.png"
                />
              ) : (
                <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                  {t(
                    'staff.personal.space.my.journeys.details.tab.home.contact.details.host.family.empty'
                  )}
                </Typography>
              )}
            </IconBox>
          ) : (
            <IconBox
              mode="inner-box"
              key={c.id}
              icon={HomeIcon}
              title={t('staff.personal.space.my.journeys.details.tab.home.contact.accommodation')}
            >
              <OverviewCard subtitle={c.name} imagePath="/college.png">
                <Badge
                  size="flexible"
                  iconPosition="start"
                  icon={
                    <div className="bg-primary-light p-0.5 rounded-full w-6 h-6 flex justify-center items-center">
                      <LocationOneIcon color={tailwindColors.primary.DEFAULT} />
                    </div>
                  }
                  color={tailwindColors.white.active}
                  borderColor={tailwindColors.grey.light.DEFAULT}
                  text={c.journeyPlace?.address ?? '-'}
                />
              </OverviewCard>
            </IconBox>
          )
        )}
    </div>
  )
}

export default StaffPersonalSpaceJourneyDetailsHome
